// frontend/src/components/TwoFactorSetup.js
// Component for setting up 2FA with QR code

import React, { useState, useEffect } from 'react';
import api from '../services/api';

const TwoFactorSetup = ({ onComplete }) => {
  const [step, setStep] = useState(1); // 1: Generate, 2: Verify, 3: Backup Codes
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const generateSecret = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/2fa/setup');
      setQrCode(response.data.qrCode);
      setSecret(response.data.secret);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate 2FA secret');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/2fa/verify', {
        token: verificationCode
      });
      
      setBackupCodes(response.data.backupCodes);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const content = `PA Quiz App - 2FA Backup Codes\nGenerated: ${new Date().toLocaleString()}\n\n${backupCodes.join('\n')}\n\nKeep these codes safe and secure!`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pa-quiz-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (step === 1) {
      generateSecret();
    }
  }, []);

  return (
    <div className="two-factor-setup">
      {step === 1 && (
        <div className="setup-step">
          <div className="setup-header">
            <h2>🔐 Set Up Two-Factor Authentication</h2>
            <p>Add an extra layer of security to your account</p>
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>Generating your 2FA secret...</p>
            </div>
          ) : error ? (
            <div className="error">
              <p>{error}</p>
              <button onClick={generateSecret} className="btn btn-primary">
                Try Again
              </button>
            </div>
          ) : null}
        </div>
      )}

      {step === 2 && (
        <div className="setup-step">
          <div className="setup-header">
            <h2>📱 Scan QR Code</h2>
            <p>Use your authenticator app to scan this QR code</p>
          </div>

          <div className="qr-section">
            {qrCode && (
              <div className="qr-code">
                <img src={qrCode} alt="2FA QR Code" style={{maxWidth: '200px', border: '1px solid #ddd', padding: '10px'}} />
              </div>
            )}
            
            <div className="manual-entry">
              <p><strong>Manual Entry Key:</strong></p>
              <code className="secret-key" style={{background: '#f5f5f5', padding: '10px', display: 'block', marginTop: '10px'}}>{secret}</code>
            </div>
          </div>

          <div className="verification-section" style={{marginTop: '20px'}}>
            <h3>Enter Verification Code</h3>
            <p>Enter the 6-digit code from your authenticator app:</p>
            
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              className="verification-input"
              maxLength="6"
              style={{fontSize: '18px', padding: '10px', textAlign: 'center', width: '150px'}}
            />

            {error && <div className="error-message" style={{color: 'red', marginTop: '10px'}}>{error}</div>}

            <div className="button-group" style={{marginTop: '15px'}}>
              <button 
                onClick={verifyAndEnable}
                disabled={loading || verificationCode.length !== 6}
                className="btn btn-primary"
                style={{padding: '10px 20px'}}
              >
                {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
              </button>
            </div>
          </div>

          <div className="app-recommendations" style={{marginTop: '20px', background: '#f9f9f9', padding: '15px', borderRadius: '5px'}}>
            <h4>Recommended Authenticator Apps:</h4>
            <ul>
              <li>Google Authenticator</li>
              <li>Authy</li>
              <li>Microsoft Authenticator</li>
              <li>1Password</li>
            </ul>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="setup-step">
          <div className="setup-header">
            <h2>✅ 2FA Enabled Successfully!</h2>
            <p>Your account is now protected with two-factor authentication</p>
          </div>

          <div className="backup-codes-section">
            <h3>🔑 Backup Codes</h3>
            <p>Save these backup codes in a safe place. You can use them to access your account if you lose your phone:</p>
            
            <div className="backup-codes" style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', margin: '15px 0'}}>
              {backupCodes.map((code, index) => (
                <code key={index} className="backup-code" style={{background: '#f5f5f5', padding: '8px', textAlign: 'center', border: '1px solid #ddd'}}>{code}</code>
              ))}
            </div>

            <div className="backup-actions" style={{margin: '20px 0'}}>
              <button onClick={downloadBackupCodes} className="btn btn-secondary" style={{marginRight: '10px'}}>
                📥 Download Codes
              </button>
              
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(backupCodes.join('\n'));
                  alert('Backup codes copied to clipboard!');
                }}
                className="btn btn-secondary"
              >
                📋 Copy Codes
              </button>
            </div>

            <div className="warning" style={{background: '#fff3cd', border: '1px solid #ffeaa7', padding: '15px', borderRadius: '5px'}}>
              <p><strong>⚠️ Important:</strong></p>
              <ul>
                <li>Each backup code can only be used once</li>
                <li>Store them securely (password manager, safe, etc.)</li>
                <li>Don't share them with anyone</li>
              </ul>
            </div>
          </div>

          <div className="completion-actions" style={{marginTop: '20px'}}>
            <button 
              onClick={() => onComplete && onComplete()}
              className="btn btn-primary"
              style={{padding: '10px 20px'}}
            >
              Complete Setup
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSetup;